import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Stack,
    Text,
    useDisclosure
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import fetchContentfulData from 'api/contentful-fetch';
import { ExtendedNextPageContext, ServerSideProps, UnknownObjectAny } from 'global.types';
import { pageQuery } from 'graphql/queries/page';
import { graphQLErrorHandler } from 'utils/graphQLHandlers';
import { Hero } from 'components/CMS';
import ProxiedImage from 'components/common/ProxiedImage';
import rxImage from '../../public/assets/rx-number.png';
import { ChangePharmacyModal, PharmacyLocation } from 'components/stores';
import { useStore } from 'store';
import { getMaskedPhoneValue } from 'utils';
import MaskedDateInput, { dateRegexPattern } from 'components/forms/inputs/MaskedDateInput';
import LDLink from 'components/common/LDLink';
import { CREDENTIALS_SENT_TEXT, GENERIC_ERROR_MESSAGE, PHONE_NUMBER_NOT_REGISTERED_PROMPT, PRESCRIPTION_PROFILE_URL } from 'utils/constants';
import GlobalErrors from 'components/GlobalErrors';
import { useRouter } from 'next/router';
import { LDTermsAndConditionsURL, TECHNICAL_DIFFICULTIES_ERROR } from 'api/constants';

const CreateAccount = (): React.ReactElement => {
    const [pharmacySelected, setPharmacySelected] = useState<string | string[] | undefined>(
        undefined
    );
    const [pharmacyLocation, setPharmacyLocation] = useState<UnknownObjectAny>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [apiError, setApiError] = useState<string | undefined>(undefined);
    const [patientId, setPatientId] = useState<string>();
    const [userPhone, setUserPhone] = useState<string>('');
    const [userAccount, setUserAccount] = useState<string>('');
    // const [redirectInterval, setRedirectInterval] = useState(0)
    const step1Form = useForm();
    const step2Form = useForm();
    const pharmacyModalControls = useDisclosure();
    const rxImageModalControls = useDisclosure();
    const { locationStore, patientStore } = useStore();
    const { query } = useRouter();

    useEffect(() => {
        if (typeof query?.step === 'string' && parseInt(query.step) === 3) {
            setCurrentStep(3);
        }
        if (typeof query?.userPhone === 'string') {
            setUserPhone(query.userPhone);
        }
    }, [query]);

    useEffect(() => {
        if (pharmacySelected) {
            const locationData = locationStore.locations?.get(pharmacySelected);

            if (locationData) setPharmacyLocation(locationData);
        }
    }, [locationStore.locations, pharmacySelected]);

    const onSubmitStep1 = async (values: { rxnumber: string; phone: string }) => {
        setIsLoading(true);
        setApiError(undefined);
        const params = {
            ...values,
            phone: values.phone?.replace(/\D/g, '').replace(/^1/, ''),
            storetelephone: (pharmacyLocation?.pharmacyPhone || pharmacyLocation?.phone)?.replace(
                /\D/g,
                ''
            )
        };
        const res = await patientStore.verifyRxAndPhone(params);
        setIsLoading(false);
        if (res?.hasError) {
            if (res?.error?.toLowerCase() == "no pickup group") {
                setApiError(PHONE_NUMBER_NOT_REGISTERED_PROMPT);
            } else if (res?.error?.toLowerCase()?.includes("Windows socket error".toLowerCase())) {
                setApiError(TECHNICAL_DIFFICULTIES_ERROR);
            }
            else {
                setApiError(res.error);
            }
        } else {
            const rows = res?.Verifyrxt?.row;
            const match = rows?.find(
                ({ Rxnumber }: { Rxnumber: string }) => Rxnumber === values.rxnumber
            );
            if (match) {
                setPatientId(match?.patid);
                setUserPhone(values.phone?.replace(/\D/g, ''));
            }
            setCurrentStep(2);
        }
    };

    const onSubmitStep2 = async (values: { dob: string; phn: string }) => {
        setIsLoading(true);
        const params = {
            ...values,
            patid: patientId,
            storetelephone: (pharmacyLocation?.pharmacyPhone || pharmacyLocation?.phone)?.replace(
                /\D/g,
                ''
            )
        };
        const res = await patientStore.verifyDobAndPhn(params);
        setIsLoading(false);
        if (res?.hasError) {
            if (res?.error?.toLowerCase()?.includes("Windows socket error".toLowerCase())) {
                setApiError(TECHNICAL_DIFFICULTIES_ERROR);
            } else {
                setApiError(res.error);
            }
        } else if (res?.Verifybphn?.row?.[0]?.Matched === 'N') {
            setApiError(
                'Date of birth or Personal Health Number does not match our records. Please try again or contact your pharmacy.'
            );
        } else {
            // ADDNEWCARD API
            const params = {
                patid: patientId,
                storetelephone: (pharmacyLocation?.pharmacyPhone || pharmacyLocation?.phone)?.replace(
                    /\D/g,
                    ''
                )
            }
            const res = await patientStore.addNewCard(params);
            if (res?.hasError) {
                if (res?.error?.toLowerCase()?.includes("Windows socket error".toLowerCase())) {
                    setApiError(TECHNICAL_DIFFICULTIES_ERROR);
                } else {
                    setApiError(res.error ?? GENERIC_ERROR_MESSAGE);
                }
            } else {
                setUserAccount(res?.Card?.row?.[0]?.Account)
                setCurrentStep(3);
            }
        }
    };

    return (
        <>
            <Hero
                data={{
                    title: 'Create Account',
                    headlineH1: 'Create Account',
                    subhead: 'Manage your prescriptions'
                }}
            />
            <Container maxW="container.lg" paddingX={{ base: '3', lg: '0' }} marginBottom="10">
                {currentStep === 1 ? (
                    <form onSubmit={step1Form.handleSubmit(onSubmitStep1 as any)}>
                        <Stack spacing={{ base: '7', lg: '10' }}>
                            {apiError && <GlobalErrors globalErrors={[apiError]} />}
                            <Box>
                                <Text
                                    color="primary.default"
                                    fontWeight="bold"
                                    fontSize={{ base: 'lg', lg: '4xl' }}
                                >
                                    Step 1 - Find your account
                                </Text>
                                <Text fontSize={{ base: 'md', lg: 'xl' }}>All fields required</Text>
                            </Box>
                            <Stack>
                                <Flex justifyContent="space-between" width={{ base: '100%', lg: '50%' }}>
                                    <Text fontSize={{ base: "xs", md: "md" }}>Rx Number on File</Text>
                                    <Text
                                        color="gray.500"
                                        fontWeight="light"
                                        fontSize={{ base: "xs", md: "md" }}
                                        as="button"
                                        type="button"
                                        onClick={rxImageModalControls.onOpen}
                                    >
                                        What's this?
                                    </Text>
                                </Flex>
                                <Controller
                                    control={step1Form.control}
                                    name="rxnumber"
                                    defaultValue=""
                                    rules={{ required: 'This field is required' }}
                                    render={({
                                        field: { onChange, onBlur, name, value },
                                        fieldState: { error, invalid }
                                    }) => (
                                        <FormControl isInvalid={!!invalid}>
                                            <Input
                                                name={name}
                                                type="number"
                                                value={typeof value === 'string' ? value : ''}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                placeholder="6 - 8 digit number"
                                                maxLength={8}
                                                width={{ base: '100%', lg: '50%' }}
                                            />
                                            <FormErrorMessage>{error?.message}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                />
                            </Stack>
                            {pharmacySelected && pharmacyLocation ? (
                                <PharmacyLocation
                                    pharmacyLocation={pharmacyLocation}
                                    onOpen={pharmacyModalControls.onOpen}
                                />
                            ) : (
                                <Box>
                                    <Text fontSize={{ base: "xs", md: "md" }}>Choose the pharmacy where your prescription is located</Text>
                                    <Button
                                        variant="ld"
                                        marginTop="5"
                                        width={{ base: '66%', lg: '25%' }}
                                        onClick={pharmacyModalControls.onOpen}
                                    >
                                        Select a Pharmacy
                                    </Button>
                                </Box>
                            )}
                            <Stack spacing="1">
                                <FormLabel htmlFor="phone" fontSize={{ base: "xs", md: "md" }}>Cell Phone (Required to receive your temporary password)*</FormLabel>
                                <Controller
                                    control={step1Form.control}
                                    name="phone"
                                    defaultValue={patientStore?.profile?.phone || ''}
                                    rules={{
                                        required: 'Please enter a valid phone number'
                                    }}
                                    render={({
                                        field: { onChange, onBlur, value, name },
                                        fieldState: { error, invalid }
                                    }) => (
                                        <FormControl isInvalid={!!invalid}>
                                            <Input
                                                name={name}
                                                value={getMaskedPhoneValue(value)}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                placeholder="Enter Phone Number"
                                                width={{ base: '100%', lg: '50%' }}
                                            />
                                            <FormLabel htmlFor="phone" color="gray.500" fontSize={{ base: "sm" }} marginTop={1}>*Cell Phone must be on the file at pharmacy</FormLabel>
                                            <FormErrorMessage>{error?.message}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                />
                            </Stack>
                            <Flex justifyContent="center">
                                <Button
                                    isLoading={isLoading}
                                    variant="ld"
                                    maxW={{ base: '200px', lg: '256px' }}
                                    flex={1}
                                    type="submit"
                                >
                                    Submit
                                </Button>
                            </Flex>
                            <Box
                                borderRadius="lg"
                                padding="6"
                                backgroundColor="shades.50"
                                marginBottom={{ base: '5', lg: '10' }}
                            >
                                <Text textAlign="center" color="gray.600" fontSize={{ base: 'sm', lg: 'md' }}>
                                    By submitting, you agree to the pharmacy <LDLink linkText='terms and conditions' href={LDTermsAndConditionsURL} target="blank" />
                                </Text>
                            </Box>
                        </Stack>
                    </form>
                ) : currentStep === 2 ? (
                    <Stack spacing={{ base: '7', lg: '10' }}>
                        <Box>
                            <Text color="primary.default" fontWeight="bold" fontSize={{ base: 'lg', lg: '4xl' }}>
                                Step 2 - Verification
                            </Text>
                            <Text fontSize={{ base: 'md', lg: 'xl' }}>All fields required</Text>
                        </Box>
                        <Box
                            borderRadius="lg"
                            padding={{ base: '3', lg: '8' }}
                            backgroundColor="shades.50"
                            marginBottom={{ base: '5', lg: '10' }}
                        >
                            <form onSubmit={step2Form.handleSubmit(onSubmitStep2 as any)}>
                                <Stack spacing={{ base: '6', lg: '8' }}>
                                    {apiError && <GlobalErrors globalErrors={[apiError]} />}
                                    <Flex direction="column" textAlign="center">
                                        <Text textAlign="center" fontWeight="bold">
                                            To create your account, please fill below details.
                                        </Text>
                                        <Text fontSize="xs">Following successful verification, a unique User ID will be assigned, and a temporary password delivered to your registered mobile number* via text.</Text>
                                    </Flex>
                                    <Flex flexDirection={{ base: 'column', lg: 'row' }}>
                                        <Controller
                                            name="dob"
                                            control={step2Form.control}
                                            rules={{
                                                required: 'This field is required',
                                                validate: (val) => {
                                                    const validity = val.match(dateRegexPattern)
                                                        ? undefined
                                                        : 'Please enter a valid date.';

                                                    return validity;
                                                }
                                            }}
                                            render={({
                                                field: { onBlur, onChange, value, name },
                                                fieldState: { error, invalid }
                                            }) => (
                                                <FormControl
                                                    isInvalid={!!invalid}
                                                    marginRight={{ base: '0', lg: '3' }}
                                                    marginBottom={{ base: '3', lg: '0' }}
                                                    flex="1"
                                                >
                                                    <FormLabel htmlFor={name}>Enter your Birthdate (MM/DD/YYYY)</FormLabel>
                                                    <MaskedDateInput
                                                        label="Date of birth (MM/DD/YYYY)"
                                                        name={name}
                                                        type="datetime"
                                                        value={value}
                                                        onBlur={onBlur}
                                                        onChange={onChange}
                                                        tone="critical"
                                                        message={error?.message}
                                                        backgroundColor="white"
                                                    />
                                                    <FormHelperText fontSize="x-small">*The registered mobile number must be on file at the pharmacy prior to creating the account.</FormHelperText>
                                                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                                                </FormControl>
                                            )}
                                        />
                                        <Controller
                                            control={step2Form.control}
                                            name="phn"
                                            defaultValue=""
                                            rules={{ required: 'This field is required' }}
                                            render={({
                                                field: { onChange, onBlur, name, value },
                                                fieldState: { error, invalid }
                                            }) => (
                                                <FormControl isInvalid={!!invalid} flex="1">
                                                    <FormLabel htmlFor={name}>Enter your Personal Health Number</FormLabel>
                                                    <Input
                                                        name={name}
                                                        type="number"
                                                        value={typeof value === 'string' ? value : ''}
                                                        onChange={onChange}
                                                        onBlur={onBlur}
                                                        maxLength={8}
                                                        backgroundColor="white"
                                                    />
                                                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                                                </FormControl>
                                            )}
                                        />
                                    </Flex>
                                    <Flex justifyContent="center">
                                        <Button
                                            isLoading={isLoading}
                                            variant="ld"
                                            maxW={{ base: '200px', lg: '256px' }}
                                            flex={1}
                                            type="submit"
                                        >
                                            Create Account
                                        </Button>
                                    </Flex>
                                </Stack>
                            </form>
                        </Box>
                    </Stack>
                ) : (
                    <Stack spacing={{ base: '7', lg: '9' }}>
                        <Text textAlign="center" fontWeight="bold" fontSize="2xl">
                            Your User ID is {userAccount}
                        </Text>
                        <Text textAlign="center" fontWeight="bold" fontSize="2xl">
                            {CREDENTIALS_SENT_TEXT}
                        </Text>
                        <Text textAlign="center">
                            Please save the user id & click the button below
                        </Text>
                        <Flex justifyContent="center">
                            <LDLink
                                href={PRESCRIPTION_PROFILE_URL}
                                variant="ld"
                                maxW={{ base: '200px', lg: '256px' }}
                                flex={1}
                            >
                                <Button variant="ld" width="100%">
                                    I have my Temp Password
                                </Button>
                            </LDLink>
                        </Flex>
                    </Stack>
                )}
            </Container>
            <ChangePharmacyModal {...pharmacyModalControls} setPharmacySelected={setPharmacySelected} />
            <Modal
                isCentered
                onClose={rxImageModalControls.onClose}
                isOpen={rxImageModalControls.isOpen}
                size="sm"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalCloseButton />
                    <ModalBody>
                        <Flex justifyContent="center" alignItems="center" padding="6">
                            <ProxiedImage src={rxImage} width={327} height={250} alt="RX Number Label" />
                        </Flex>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export async function getServerSideProps(context: ExtendedNextPageContext): ServerSideProps {
    const { preview = false } = context;
    const content = (await fetchContentfulData(
        { query: pageQuery, variables: { preview } },
        preview
    )) as UnknownObjectAny;

    graphQLErrorHandler('page', content?.errors, pageQuery);

    return { props: { content: content?.data?.homepage ?? null } };
}

export default CreateAccount;
